import React, { useCallback, useEffect, useState } from "react";
import { postApi } from "../../../helpers/auth_helpers";
import { isStudioModuleEntitled } from "../../../helpers/studioModules";
import AiCreditsSheet from "./AiCreditsSheet";
import { isPreviewDoc, isPreviewVideo } from "../previewMedia";
import {
  AiCredits,
  DESCRIPTION_LANGUAGES,
  canSpend,
  defaultDescriptionLanguage,
  fetchAiCredits,
  formatCredits,
  isCreditsExhausted,
  rememberDescriptionLanguage,
} from "./aiCredits";

const base = () => String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

const labelOf = (v: any): string | undefined => {
  if (!v) return undefined;
  if (typeof v === "string") return v.trim() || undefined;
  return (v.label || v.name || "").trim() || undefined;
};

/** What the model may use besides the photo. It is told never to add anything not in here or not visible. */
export const descriptionFacts = (form: any) => ({
  name: labelOf(form?.productName),
  color: labelOf(form?.variantCode),
  artform: labelOf(form?.artform),
  type: labelOf(form?.type),
  category: labelOf(form?.category),
  subCategory: labelOf(form?.subCategory),
  weaveType: labelOf(form?.weaveType),
  zariUsed: labelOf(form?.zariUsed),
  yarnType: labelOf(form?.yarnType),
  timeTaken: labelOf(form?.productionTimeInMonths),
  weavingCenter: labelOf(form?.weavingCenter),
  designType: labelOf(form?.typeOfSareeByDesign),
});

/**
 * Up to 3 uploaded photos (videos skipped), cover first. Media-service photos go by asset id (the server reads them itself);
 * older ones by their S3 link. Photos still on the device are skipped.
 */
export const photoRefs = (photos: any[]): { imageUrls: string[]; mediaAssetIds: string[]; count: number } => {
  // Videos and documents can sit in the same list; only photos count toward the three.
  const rows = (photos || []).filter((p) => p && p.show !== false && !p._local && !isPreviewVideo(p) && !isPreviewDoc(p));
  const pinned = (p: any) => p?.pin === true || p?.pin === 1 || p?.pin === "1";
  const sorted = [...rows].sort((a, b) => Number(pinned(b)) - Number(pinned(a)));
  const imageUrls: string[] = [];
  const mediaAssetIds: string[] = [];
  sorted.forEach((p) => {
    if (imageUrls.length + mediaAssetIds.length >= 3) return;
    if (p?.mediaAssetId) mediaAssetIds.push(String(p.mediaAssetId));
    else {
      const url = p?.mediaURL || p?.blobImage || "";
      if (/^https:\/\//.test(url)) imageUrls.push(url);
    }
  });
  return { imageUrls, mediaAssetIds, count: imageUrls.length + mediaAssetIds.length };
};

type Suggestion = { text: string; language: string; chargedPaise: number | null };

type Props = {
  form: any;
  photos: any[];
  onWrite: (text: string) => void;
};

/**
 * "Write from photos": Claude writes a short description from the cover photo and the details typed so far,
 * in the language picked here. It is only a suggestion until the user presses "Use this". Each try costs AI
 * credits (shown before and after).
 */
const WriteFromPhotos = ({ form, photos, onWrite }: Props) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [language, setLanguage] = useState<string>(defaultDescriptionLanguage);
  const [credits, setCredits] = useState<AiCredits | null>(null);
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [draft, setDraft] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const entitled = isStudioModuleEntitled("AI");

  useEffect(() => {
    if (entitled) fetchAiCredits().then(setCredits);
  }, [entitled]);
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  if (!entitled) return null;
  const refs = photoRefs(photos);
  const estimate = credits?.estimatePaise?.product_description;
  const allowed = canSpend(credits);

  const run = async () => {
    if (!refs.count || busy) return;
    if (!allowed) {
      setSheetOpen(true);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res: any = await postApi(`${base()}/product/ai-description`, {
        imageUrls: refs.imageUrls,
        mediaAssetIds: refs.mediaAssetIds.length ? refs.mediaAssetIds : undefined,
        facts: descriptionFacts(form),
        existingDescription: form?.description || undefined,
        language,
      });
      if (isCreditsExhausted(res)) {
        setError("You’re out of AI credits.");
        setSheetOpen(true);
        return;
      }
      const text = String(res?.data?.description ?? "").trim();
      if (res?.success === false || !text) {
        setError(res?.message || "Couldn't write it right now. Try again in a minute.");
        return;
      }
      const chargedPaise = res?.data?.chargedPaise != null ? Number(res.data.chargedPaise) : null;
      if (res?.data?.balancePaise != null && credits) {
        setCredits({ ...credits, balancePaise: Number(res.data.balancePaise) });
      }
      setSuggestion({ text: text.slice(0, 2000), language, chargedPaise });
      setDraft(text.slice(0, 2000));
    } catch {
      setError("Couldn't write it right now. Try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  const accept = () => {
    if (!draft.trim()) return;
    onWrite(draft.trim().slice(0, 2000));
    setSuggestion(null);
    setDraft("");
  };

  return (
    <div className="ps-ai-box">
      <div className="ps-ai">
        <label className="ps-ai__lang">
          <span>Language</span>
          <select
            value={language}
            onChange={(e) => {
              setLanguage(e.target.value);
              rememberDescriptionLanguage(e.target.value);
            }}
          >
            {DESCRIPTION_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="ps-btn ps-btn--line ps-btn--sm"
          onClick={run}
          disabled={!refs.count || busy}
        >
          <span>{busy ? "Writing…" : suggestion ? "✨ Write again" : "✨ Write from photos"}</span>
        </button>
        <button type="button" className="ps-ai__credits" onClick={() => setSheetOpen(true)}>
          {estimate != null ? `≈ ${formatCredits(estimate)} · ` : ""}
          {credits ? `balance ${formatCredits(credits.balancePaise)}` : "AI credits"}
        </button>
      </div>
      <p className="ps-ai__hint" role="status">
        {error ||
          (!refs.count
            ? "Add a photo first (it must finish uploading)."
            : !allowed
              ? "Balance used up — add credits to write more."
              : `Uses your ${refs.count === 1 ? "cover photo" : `${refs.count} photos`} and the details above. You check it before it goes in.`)}
      </p>

      {suggestion ? (
        <div className="ps-ai__review" role="region" aria-label="Suggested description">
          <div className="ps-label">
            <span>Suggested description · {suggestion.language}</span>
            <i>{draft.length} / 2000</i>
          </div>
          <textarea
            className="form-control"
            rows={5}
            maxLength={2000}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <p className="ps-ai__hint">
            Read it and fix anything that isn’t true — buyers see this on the product and certificate.
            {suggestion.chargedPaise != null ? ` Charged ${formatCredits(suggestion.chargedPaise)}.` : ""}
          </p>
          <div className="ps-ai__actions">
            <button type="button" className="ps-btn ps-btn--line" onClick={() => setSuggestion(null)}>
              <span>Discard</span>
            </button>
            <button type="button" className="ps-btn ps-btn--blue ps-btn--wide" onClick={accept} disabled={!draft.trim()}>
              <span>{form?.description ? "Replace description" : "Use this"}</span>
            </button>
          </div>
        </div>
      ) : null}

      <AiCreditsSheet open={sheetOpen} onClose={closeSheet} onChanged={setCredits} />
    </div>
  );
};

export default WriteFromPhotos;
